import React, { useEffect, useState } from 'react';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import Overlay from '../Overlay';
import {
  OUTCOMES,
  PRICE_ACCEPTED,
  outcomeFor,
  reasonsFor,
} from '../../lib/invoiceDiscrepancy';

// #301, Session 74z4. Ends a flagged discrepancy, with the outcome recorded.
//
// A NEW COMPONENT RATHER THAN A GENERALISED ShortCloseSheet, deliberately.
// Standing rule 14 says extract when the duplication is in the mutation and its
// side effects, not when two screens merely share a silhouette. Here the subject
// differs (an invoice, not an order line), the RPC differs, the audit table
// differs, and this sheet asks a question ShortCloseSheet does not ask at all —
// which of two outcomes happened. Generalising the two into one would buy
// indirection, which is the trade shortClose.js itself documents declining.
//
// Rendered through Overlay's portal, never position: fixed (locked rule 3).
export default function ResolveDiscrepancySheet({
  visible, invoice, req, isMobile,
  saving, error, done, result,
  onSubmit, onClose,
}) {
  const [outcome, setOutcome] = useState(null);
  const [reason, setReason] = useState('');

  // Reset when the sheet opens on a different invoice. Carrying an outcome or a
  // sentence from the previous invoice would pre-fill an audit record with a
  // decision made about something else.
  useEffect(() => {
    if (visible) { setOutcome(null); setReason(''); }
  }, [visible, invoice?.id]);

  if (!visible || !invoice) return null;

  const chosen = outcomeFor(outcome);
  const trimmed = reason.trim();
  const canSubmit = !!chosen && trimmed.length > 0 && !saving;

  const accent = '#B31F7C';
  const amount = invoice.discrepancy_amount != null ? parseFloat(invoice.discrepancy_amount) : null;
  const total = invoice.total_amount != null ? parseFloat(invoice.total_amount) : null;

  function fmt(n) {
    if (n == null || isNaN(n)) return '—';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} maxWidth={520} onClose={saving ? undefined : onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '12px auto 0', flexShrink: 0 }} />}

        <div style={{ background: '#0F0F0F', padding: '14px 20px', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>Resolve discrepancy</div>
          <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 2 }}>
            {invoice.invoice_ref || 'No invoice number'}
            {req?.request_number ? ' · ' + req.request_number : ''}
          </div>
        </div>

        {done ? (
          <div style={{ padding: '20px 20px 32px', overflowY: 'auto' }}>
            <div style={{ background: '#E8F5E9', borderRadius: 12, padding: 20, border: '1px solid #C8E6C9', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2E7D32' }}>Discrepancy resolved</div>
              {/* The function reports how many open discrepancies the order has
                  left. Saying so here is the only place a person finds out the
                  order has left the Discrepancies tab. */}
              {result?.order_cleared && (
                <div style={{ fontSize: 12, color: '#2E7D32', marginTop: 4 }}>
                  {req?.request_number || 'This order'} has no open discrepancies left.
                </div>
              )}
              {result && !result.order_cleared && result.open_discrepancies_remaining > 0 && (
                <div style={{ fontSize: 12, color: '#2E7D32', marginTop: 4 }}>
                  {result.open_discrepancies_remaining} still open on this order.
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{ width: '100%', padding: 14, borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none', background: accent, color: 'white', cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        ) : (
          <div style={{ padding: '16px 20px 28px', overflowY: 'auto' }}>
            {error && (
              <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
                {error}
              </div>
            )}

            {/* The figures, and only the ones actually recorded. discrepancy_amount
                is stored as ABS(billed - expected), so the DIRECTION is not in the
                database. Rendering "over" would be inventing it — the one thing a
                money screen must not do. */}
            <div style={{ background: '#FFF5F6', border: '1px solid #F3C6C6', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#501313', marginBottom: 3 }}>
                <span>Invoice total</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, fontWeight: 700, color: '#A32D2D', borderTop: '1px solid #F3C6C6', paddingTop: 4, marginTop: 4 }}>
                <span>Flagged difference</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(amount)}</span>
              </div>
            </div>

            <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 7 }}>
              What was the outcome? <span style={{ color: '#A32D2D' }}>*</span>
            </div>

            {/* The outcome is the reason code, and the sentence under each option
                is load-bearing rather than help text: it is the only place the
                screen says that one of these two removes the invoice from the
                first-pass match rate. Without it both options look identical and
                whichever sits nearer gets picked. */}
            {OUTCOMES.map(o => {
              const on = outcome === o.code;
              return (
                <button
                  key={o.code}
                  type="button"
                  onClick={() => { setOutcome(on ? null : o.code); setReason(''); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                    borderRadius: 8, padding: '10px 12px', marginBottom: 8,
                    background: on ? '#FDF2F8' : 'white',
                    border: on ? '2px solid ' + accent : '1px solid #E0E0E0',
                  }}
                >
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: on ? '#7A1454' : '#3C3C3C' }}>{o.label}</div>
                  <div style={{ fontSize: 11, color: on ? '#7A1454' : '#7A7A7A', marginTop: 3, lineHeight: 1.45 }}>{o.effect}</div>
                </button>
              );
            })}

            {!chosen && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start', background: '#F7F7F6',
                border: '1px solid #E8E8E8', borderLeft: '3px solid #9A9A9A',
                borderRadius: '0 7px 7px 0', padding: '9px 12px', marginTop: 8,
              }}>
                <IconInfoCircle size={14} color="#5C5C5C" style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 11.5, color: '#5C5C5C', lineHeight: 1.5 }}>
                  Resolving does not change what was billed or paid. It records what happened and takes the order off the Discrepancies tab.
                </span>
              </div>
            )}

            {chosen && (
              <>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: 0.4, margin: '14px 0 7px' }}>
                  {chosen.prompt} <span style={{ color: '#A32D2D' }}>*</span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {reasonsFor(chosen.code).map(c => {
                    const on = trimmed === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setReason(on ? '' : c)}
                        style={{
                          fontSize: 11.5, fontWeight: 600, borderRadius: 20, padding: '5px 11px',
                          cursor: 'pointer',
                          background: on ? '#FFF8EC' : '#F5F5F3',
                          border: '1px solid ' + (on ? '#F3D9A8' : '#E4E4E0'),
                          color: on ? '#8A5A08' : '#3C3C3C',
                        }}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>

                {/* 16px minimum, locked rule 12 — anything smaller triggers iOS
                    auto-zoom, and this is a mobile-first surface. */}
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Pick one above, or type what happened"
                  rows={3}
                  style={{
                    width: '100%', border: '1px solid #E0E0E0', borderRadius: 8,
                    padding: '10px 12px', fontSize: 16, color: '#0F0F0F',
                    fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
                  }}
                />
              </>
            )}

            <div style={{ display: 'flex', gap: 9, marginTop: 16 }}>
              <button
                onClick={onClose}
                disabled={saving}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 10, fontSize: 13.5, fontWeight: 600,
                  background: 'white', color: '#0F0F0F', border: '1px solid #E0E0E0',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => onSubmit({ outcome, reason: trimmed })}
                disabled={!canSubmit}
                style={{
                  flex: 2, padding: '12px 0', borderRadius: 10, fontSize: 13.5, fontWeight: 700,
                  border: 'none',
                  background: canSubmit ? accent : '#E0E0E0',
                  color: canSubmit ? 'white' : '#9A9A9A',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Saving…' : (chosen ? chosen.confirm : 'Resolve')}
              </button>
            </div>

            {chosen && !trimmed && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 9, fontSize: 11, color: '#9A9A9A' }}>
                <IconAlertTriangle size={12} color="#BA7517" />
                A reason is required — it is the only record of why this difference was {chosen.code === PRICE_ACCEPTED ? 'paid' : 'dismissed'}.
              </div>
            )}
          </div>
        )}
      </div>
    </Overlay>
  );
}
